import { useFormik } from 'formik'
import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import * as Yup from 'yup'
import { Button } from '../../components/button'
import { CategoryCard } from '../../components/cards/categoryCard'
import { ImageBanner } from '../../components/images/imageBanner'
import { EditInput } from '../../components/inputs/editInput'
import { ImageUploadPreviewModal } from '../../components/modal/app_modals/ImagePreviewModal'
import { SelectCategoriesModal } from '../../components/modal/app_modals/selectCategoriesModal'
import Spinner from '../../components/spinner/Spinner'
import { useModalControl } from '../../hooks/useModalControl'
import {
  useCreateListMutation,
  useEditListMutation,
  useFetchListQuery,
} from '../../redux-toolkits/lists/list.slice'
import { SingleEssential } from '../../redux-toolkits/lists/list.types'

const CreateEssential = () => {
  const [editIndex, setEditIndex] = useState<number>()

  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()

  const { isLoading: fetchingList, data } = useFetchListQuery(
    {
      id: id ?? '',
    },
    {
      skip: !id,
    },
  )

  const { handleModalOpen, setModalOpen, modal, modalOpen, handleModalClose } =
    useModalControl()

  const [uploadedImage, setUploadedImage] = useState<File>()
  const [uploadedIcon, setUploadedIcon] = useState<File>()
  const [list, setList] = useState<SingleEssential | null>()
  const [createList, { isLoading: creatingList }] = useCreateListMutation()
  const [editList, { isLoading: updatingList }] = useEditListMutation()
  const [uploadMode, setUploadMode] = useState('')
  const imageRef = useRef<HTMLInputElement | null>(null)
  const iconRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (data?.data) {
      setList(data.data)
    }
  }, [data?.data])

  const handleFileChange = (
    event: ChangeEvent<HTMLInputElement>,
    bannerOrIcon = 'banner',
  ) => {
    if (!event.target.files) {
      return
    }
    setUploadMode(bannerOrIcon)
    const selectedFile = event.target.files[0]
    bannerOrIcon === 'banner'
      ? setUploadedImage(selectedFile)
      : setUploadedIcon(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const onCreate = () => {
    navigate('/lists/curated')
  }

  const formik = useFormik({
    initialValues: {
      title: list?.title ?? '',
      summary: list?.summary ?? '',
      category: list?.category ?? [],
      banner: list?.banner ?? '',
      icon: list?.icon ?? '',
    },
    validationSchema: Yup.object({
      title: Yup.string().required('Required'),
      summary: Yup.string().required('Required'),
      category: Yup.array()
        .of(Yup.object().required('Required'))
        .min(1, 'Required'),
      banner: Yup.string().required('Required'),
      icon: Yup.string().required('Required'),
    }),
    onSubmit: async (values) => {
      if (id) {
        await editList({
          body: { ...values, category: values.category.map((cat) => cat._id) },
          id,
        })?.then((resp) => {
          if (resp.data?.success) {
            onCreate()
          }
        })
      } else {
        await createList({
          ...values,
          category: values.category.map((cat) => cat._id),
        })?.then((resp) => {
          if (resp.data?.success) {
            onCreate()
          }
        })
      }
    },
    enableReinitialize: true,
  })

  return (
    <div>
      {id && fetchingList ? (
        <Spinner fullScreen={false} />
      ) : (
        <form onSubmit={formik.handleSubmit} className="flex flex-col gap-10">
          <div className="flex items-center justify-end gap-4">
            <Button
              type="button"
              onClick={onCreate}
              label="Cancel"
              size="medium"
              className="!px-10"
            />

            <Button
              type="submit"
              label="Save"
              primary
              disabled={creatingList || updatingList || !formik.isValid}
              loading={creatingList || updatingList}
              size="medium"
              className="!px-10"
            />
          </div>

          <div className="relative">
            <ImageBanner
              imageUrl={formik.values.banner}
              loading={false}
              errors={formik.errors.banner ? formik.errors.banner : ''}
              // onDelete={() => {}}
              onImageEdit={(e) => {
                handleFileChange(e, 'banner')
              }}
              editIconPos="top-right"
            />

            <ImageBanner
              imageUrl={formik.values.icon}
              loading={false}
              className="!rounded-full !w-[80px] !h-[80px] !absolute left-4 -bottom-[20px] z-10"
              errors={formik.errors.icon ? formik.errors.icon : ''}
              // onDelete={() => {}}
              onImageEdit={(e) => {
                handleFileChange(e, 'icon')
              }}
            />
          </div>

          <div className="flex-1 grid grid-cols-1 gap-6">
            <EditInput
              title="Essential title"
              value={formik.values.title}
              onChange={formik.handleChange}
              type="text"
              placeholder="Essential title"
              onEditSave={() => {
                setEditIndex(undefined)
              }}
              editing={editIndex === 1}
              onEditClick={() => {
                setEditIndex(1)
              }}
              onCancel={() => {
                if (id) {
                  formik.setFieldValue('title', '')
                } else {
                  formik.setFieldValue('title', data?.data?.title)
                }
                setEditIndex(undefined)
              }}
              loading={false}
              inputName="title"
              mode={id ? 'edit' : 'edit'}
            />

            <EditInput
              title="Essential summary"
              value={formik.values.summary}
              onChange={formik.handleChange}
              type="textarea"
              placeholder="Essential summary"
              onEditSave={() => {
                setEditIndex(undefined)
              }}
              editing={editIndex === 2}
              onEditClick={() => {
                setEditIndex(2)
              }}
              onCancel={() => {
                if (id) {
                  formik.setFieldValue('summary', '')
                } else {
                  formik.setFieldValue('summary', data?.data?.summary)
                }
                setEditIndex(undefined)
              }}
              loading={false}
              inputName="summary"
              mode={id ? 'edit' : 'edit'}
            />

            <div className="grid grid-cols-1 gap-4">
              <div className="flex items-center gap-4 justify-between">
                <p>
                  <span className="font-bold">Categories: </span>
                  <span className="text-danger">
                    {formik.errors.category
                      ? `(${formik.errors.category})`
                      : ''}
                  </span>
                </p>

                <Button
                  primary
                  size="medium"
                  label="Manage categories"
                  onClick={() => {
                    handleModalOpen('selectCategoriesModal')
                  }}
                  className="!max-w-fit"
                />
              </div>

              {formik.values.category.length > 0 ? (
                <div className="flex flex-row gap-4 items-center flex-wrap">
                  {formik.values.category.map((category, idx) => {
                    const key = `tag-${idx}`
                    return <CategoryCard key={key} category={category} />
                  })}
                </div>
              ) : (
                <p className="text-sec-black">No categories added</p>
              )}
            </div>
          </div>

          {uploadedImage || uploadedIcon ? (
            <ImageUploadPreviewModal
              image={uploadedImage || (uploadedIcon as File)}
              open={modal === 'imageUploadPreviewModal' && modalOpen}
              setOpen={setModalOpen}
              onCloseCallback={() => {
                uploadedImage
                  ? setUploadedImage(undefined)
                  : setUploadedIcon(undefined)
              }}
              uploadMode={uploadMode}
              onSuccessCallback={(url) => {
                formik.setFieldValue(uploadedImage ? 'banner' : 'icon', url)
              }}
            />
          ) : null}

          <SelectCategoriesModal
            open={modalOpen && modal === 'selectCategoriesModal'}
            setOpen={setModalOpen}
            onCloseCallback={() => {
              handleModalClose()
            }}
            onSuccessCallback={(categories) => {
              formik.setFieldValue('category', categories)
              handleModalClose()
            }}
            selectedCategories={formik.values.category}
            maxSelection={undefined}
          />
        </form>
      )}
    </div>
  )
}

export default CreateEssential
